import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowCounterClockwise";
import { useEffect, useRef, useState } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import { HarnessChatEmptyState } from "@/onboarding/components/harness-chat/harness-chat-empty-state";
import { HarnessChatInput } from "@/onboarding/components/harness-chat/harness-chat-input";
import { IconButtonWithTooltip } from "@/payroll/ai-script/icon-button-with-tooltip";
import { AI_SCRIPT_CHAT_CONTEXT_KIND } from "@/payroll/ai-script/types";
import type { AiScriptChatSelectionContext } from "@/payroll/ai-script/types";
import { ChatBubble } from "./chat-bubble";
import {
  CHAT_PANEL_ICON_BUTTON_CLASS,
  ChatPanelHeader,
} from "./chat-panel-header";
import { LiveAssistantMessage } from "./live-assistant-message";
import { SelectionContextChip } from "./selection-context-chip";
import type { ChatMessage } from "./types";

export const DEMO_CHAT_PROMPT =
  "What should I review before approving this payroll?";
export const DEMO_PAYMENT_CHAT_PROMPT = "Tell me about this payment";

export const DEMO_CHAT_RESPONSE = `I reviewed this run against **Senior Service Technician Plan v3**, effective July 1.

### Pay-period summary

- **12 technicians** generated **$121,450.00** in eligible service revenue.
- The plan produced **$10,684.25** in commission and performance pay.
- **9 technicians** were paid on commission; **3 technicians** received their hourly guarantee instead.
- **Jordan Lee** had the highest variable payout at **$1,486.00**, driven by $17,325.00 of eligible revenue and a $100 five-star-review bonus.

### Items I checked

1. Revenue below **$8,000** was paid at **6%**; revenue above that threshold was paid at **8%**.
2. Each result was compared with the technician's **$18/hour guarantee** using approved hours.
3. Callback deductions were capped at **$150 per technician**, as required by the plan.
4. Two manual adjustments totaling **$225.00** were included and both have manager notes.

I didn't find any blocking calculation issues. The three hourly-floor payments and Jordan Lee's top payout are the best items to spot-check before approval.`;

export const buildDemoPaymentResponse = (
  context: AiScriptChatSelectionContext,
) => {
  switch (context.kind) {
    case AI_SCRIPT_CHAT_CONTEXT_KIND.cell: {
      const technician = context.rowLabel ?? "this technician";
      const paymentType = context.columnHeader ?? "payment";

      return `This **${paymentType}** for **${technician}** was calculated under **Senior Service Technician Plan v3**.

### Calculation

- First **$8,000.00** of eligible service revenue × **6%** = **$480.00**
- Remaining **$3,000.00** × **8%** = **$240.00**
- Quality-score bonus for maintaining 4.8+ stars = **$150.00**
- Callback deduction for job #18432 = **−$27.50**
- **Calculated commission: $842.50**

The plan then compared that result with the hourly guarantee: **40 approved hours × $18.00 = $720.00**. Because **$842.50** is higher, the commission result was selected for **${context.sheetName} · ${context.coordinate}**.

There are no manual overrides on this payment. The only adjustment is the $27.50 callback deduction defined by the pay plan.`;
    }
    case AI_SCRIPT_CHAT_CONTEXT_KIND.row:
      return `This row summarizes the payroll calculation for **${context.rowLabel ?? `row ${context.rowNumber}`}**. Volca applied the matching commission rules, compared the result with the hourly guarantee, and included any eligible adjustments across the ${context.cells.length} values shown here.`;
    case AI_SCRIPT_CHAT_CONTEXT_KIND.column:
      return `The **${context.columnHeader ?? `column ${context.columnLetter}`}** values are calculated consistently from each technician's eligible revenue and pay-plan rules. Volca then compares each result with the applicable hourly guarantee before selecting the final payment.`;
  }
};

export const DEMO_CHAT_LOADING_DELAY_MS = 900;
export const DEMO_CHAT_TYPEWRITER_INTERVAL_MS = 24;
const DEMO_CHAT_TYPEWRITER_CHUNK_SIZE = 4;

const DEMO_CHAT_PHASE = {
  idle: "idle",
  loading: "loading",
  responding: "responding",
  complete: "complete",
} as const;

type DemoChatPhase = (typeof DEMO_CHAT_PHASE)[keyof typeof DEMO_CHAT_PHASE];

interface DemoAiScriptExplanationChatSidebarProps {
  selectionContext?: AiScriptChatSelectionContext | null;
  pendingQuestion?: string | null;
  onQuestionPrepared?: () => void;
  onClearSelectionContext?: () => void;
  onClose: () => void;
}

const buildMessage = (
  id: string,
  role: ChatMessage["role"],
  content: string,
): ChatMessage => ({ id, role, content });

export const DemoAiScriptExplanationChatSidebar = ({
  selectionContext,
  pendingQuestion,
  onQuestionPrepared,
  onClearSelectionContext,
  onClose,
}: DemoAiScriptExplanationChatSidebarProps) => {
  const [phase, setPhase] = useState<DemoChatPhase>(DEMO_CHAT_PHASE.idle);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [displayedResponse, setDisplayedResponse] = useState("");
  const displayedResponseLengthRef = useRef(0);
  const hasCompletedResponseRef = useRef(false);
  const activeResponseRef = useRef(DEMO_CHAT_RESPONSE);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!pendingQuestion) return;
    setDraft(pendingQuestion);
    textareaRef.current?.focus();
    onQuestionPrepared?.();
  }, [pendingQuestion, onQuestionPrepared]);

  useEffect(() => {
    if (phase !== DEMO_CHAT_PHASE.loading) return undefined;

    const timeout = window.setTimeout(
      () => setPhase(DEMO_CHAT_PHASE.responding),
      DEMO_CHAT_LOADING_DELAY_MS,
    );

    return () => window.clearTimeout(timeout);
  }, [phase]);

  useEffect(() => {
    if (phase !== DEMO_CHAT_PHASE.responding) return undefined;

    const interval = window.setInterval(() => {
      if (hasCompletedResponseRef.current) return;

      const nextResponse = activeResponseRef.current.slice(
        0,
        displayedResponseLengthRef.current + DEMO_CHAT_TYPEWRITER_CHUNK_SIZE,
      );
      displayedResponseLengthRef.current = nextResponse.length;
      setDisplayedResponse(nextResponse);

      if (nextResponse.length === activeResponseRef.current.length) {
        hasCompletedResponseRef.current = true;
        window.clearInterval(interval);
        setMessages((currentMessages) => [
          ...currentMessages,
          buildMessage(
            "demo-assistant",
            "assistant",
            activeResponseRef.current,
          ),
        ]);
        setPhase(DEMO_CHAT_PHASE.complete);
      }
    }, DEMO_CHAT_TYPEWRITER_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [phase]);

  const handleStart = (prompt: string) => {
    if (
      phase === DEMO_CHAT_PHASE.loading ||
      phase === DEMO_CHAT_PHASE.responding
    )
      return;

    activeResponseRef.current = selectionContext
      ? buildDemoPaymentResponse(selectionContext)
      : DEMO_CHAT_RESPONSE;
    setMessages([
      {
        ...buildMessage("demo-user", "user", prompt),
        selectionContext: selectionContext ?? undefined,
      },
    ]);
    setDraft("");
    setDisplayedResponse("");
    displayedResponseLengthRef.current = 0;
    hasCompletedResponseRef.current = false;
    setPhase(DEMO_CHAT_PHASE.loading);
  };

  const handleReset = () => {
    setMessages([]);
    setDisplayedResponse("");
    displayedResponseLengthRef.current = 0;
    hasCompletedResponseRef.current = false;
    setPhase(DEMO_CHAT_PHASE.idle);
  };

  const handleSendDraft = () => {
    const prompt = draft.trim();
    if (!prompt) return;

    handleStart(prompt);
  };

  const isBusy =
    phase === DEMO_CHAT_PHASE.loading || phase === DEMO_CHAT_PHASE.responding;

  return (
    <Stack align="stretch" className="h-full min-h-0 w-full" gap={0}>
      <ChatPanelHeader
        title="Payroll chat"
        subtitle="Ask about this pay period's results"
        onClose={onClose}
        actions={
          <IconButtonWithTooltip
            label="Reset demo chat"
            onClick={handleReset}
            disabled={phase === DEMO_CHAT_PHASE.idle}
            className={CHAT_PANEL_ICON_BUTTON_CLASS}
          >
            <ArrowCounterClockwiseIcon />
          </IconButtonWithTooltip>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <Stack align="stretch" gap={0} className="w-full p-4">
          {phase === DEMO_CHAT_PHASE.idle ? (
            <HarnessChatEmptyState
              title="Ask about this pay period"
              description="Find out who was paid what, and why."
              prompts={[
                ...(selectionContext ? [DEMO_PAYMENT_CHAT_PROMPT] : []),
                DEMO_CHAT_PROMPT,
              ]}
              disabled={false}
              onSend={handleStart}
            >
              {selectionContext && (
                <Stack align="start" direction="row" gap={0}>
                  <SelectionContextChip
                    context={selectionContext}
                    onDismiss={onClearSelectionContext}
                  />
                </Stack>
              )}
            </HarnessChatEmptyState>
          ) : (
            messages.map((message, index) => (
              <ChatBubble
                key={message.id}
                message={message}
                prevRole={messages[index - 1]?.role}
              />
            ))
          )}

          {isBusy && (
            <LiveAssistantMessage
              displayedLiveText={displayedResponse}
              progressSteps={[]}
            />
          )}
        </Stack>
      </div>

      <HarnessChatInput
        draft={draft}
        disabled={isBusy}
        textareaRef={textareaRef}
        onDraftChange={setDraft}
        onSend={handleSendDraft}
        placeholder="Ask about this payroll..."
        header={
          phase !== DEMO_CHAT_PHASE.idle && selectionContext ? (
            <Stack align="start" direction="row" gap={0} className="mb-2">
              <SelectionContextChip
                context={selectionContext}
                onDismiss={onClearSelectionContext}
              />
            </Stack>
          ) : undefined
        }
      />
    </Stack>
  );
};
